import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { User, Settings, LogOut, Building2, Mail } from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';
import { Link, useNavigate } from 'react-router';
import { usePermissions } from '@/hooks/usePermissions';
import { getRoleText } from '@/helpers/enumStylers';
import { formatDate } from '@/helpers/formatDate';
import { formatDistanceToNow } from 'date-fns';

export default function UserProfileDropdown() {
  const { user, organization, logout } = useAuth();

  const navigate = useNavigate();
  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };
  const [isOpen, setIsOpen] = useState(false);

  const { isAdmin } = usePermissions({});

  if (!user || !organization) return null;

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="relative h-9 w-9 rounded-full hover:bg-background"
        >
          <Avatar className="h-8 w-8 ring-2 dark:ring-secondary shadow-sm">
            <AvatarFallback>{user.name[0].toUpperCase()}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        {/* User Info Header */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12 ring-2 dark:ring-secondary">
              <AvatarFallback>{user.name[0].toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-foreground truncate">
                {user.name}
              </div>
              <div className="flex items-center gap-2 mt-1">
                <Badge
                  variant="outline"
                  // className={`bg-gradient-to-r ${getRoleColor(
                  //   user.role,
                  // )} text-white border-none text-xs`}
                >
                  {/* {getRoleIcon(user.role)} */}
                  <span className="ml-1">{getRoleText(user.role)}</span>
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Organization Info */}
        <div className="p-4 border-b border-border bg-background/50">
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="h-4 w-4 text-foreground" />
            <span className="text-sm font-medium text-foreground">
              Organization
            </span>
          </div>
          <div className="space-y-1">
            <div className="font-semibold text-foreground">
              {organization?.name}
            </div>
          </div>
        </div>

        {/* User Details */}
        <div className="p-4 border-b border-border">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              {/* <Mail className="h-3 w-3 text-gray-500" /> */}
              <span className="text-foreground">Email:</span>
              <span className="font-medium text-foreground truncate">
                {user.email}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-foreground">Domain:</span>
              <span className="font-medium text-foreground">
                {organization.domain}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-foreground">User Since:</span>
              <span className="font-medium text-foreground">
                {formatDate(user?.createdAt || '')}
              </span>
            </div>
            {/* {user.role === 'CREW' && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-foreground">Last Active:</span>
                <span className="font-medium text-foreground">
                  {user.
                    ? formatDistanceToNow(new Date(user.lastActiveAt), {
                        addSuffix: true,
                      })
                    : 'N/A'}
                </span>
              </div>
            )} */}
          </div>
        </div>

        {/* Menu Items */}
        <div className="py-1">
          {/* <DropdownMenuItem asChild className="cursor-pointer">
            <Link to="/profile" className="flex items-center gap-2 px-4 py-2">
              <User className="h-4 w-4" />
              <span>Profile Settings</span>
            </Link>
          </DropdownMenuItem> */}

          {isAdmin && (
            <DropdownMenuItem asChild className="cursor-pointer">
              <Link
                to={`/${user.role.toLowerCase()}/settings` || '/'}
                className="flex items-center gap-2 px-4 py-2"
              >
                <Settings className="h-4 w-4 text-foreground" />
                <span>Settings</span>
              </Link>
            </DropdownMenuItem>
          )}
        </div>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          className="cursor-pointer text-red-600 hover:bg-red-50 hover:text-red-700 px-4 py-2"
          onClick={handleLogout}
        >
          <LogOut className="h-4 w-4 mr-2" />
          <span>Sign Out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
